import { BusinessProfileDropdown } from '@/components/BusinessProfile/BusinessProfileDropdown'
import { BusinessProfileModal } from '@/components/BusinessProfile/BusinessProfileModal'
import { BusinessProfile } from '@/lib/features/businessProfile/types'
import { PAGE_PATH } from '@/lib/page-paths'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useRef, useState } from 'react'
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi'

type SidebarHeaderProps = {
  isCollapsed: boolean
  isPermanentlyCollapsed: boolean
  isHovering: boolean
  onToggleCollapse: () => void
}

export function SidebarHeader({
  isCollapsed,
  isPermanentlyCollapsed,
  isHovering,
  onToggleCollapse,
}: SidebarHeaderProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Business Profile modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    mode: 'create' | 'edit' | 'delete'
    profile?: BusinessProfile
  }>({
    isOpen: false,
    mode: 'create',
  })

  const openCreateModal = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
    })
  }

  const openEditModal = (profile: BusinessProfile) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      profile,
    })
  }

  const openDeleteModal = (profile: BusinessProfile) => {
    setModalState({
      isOpen: true,
      mode: 'delete',
      profile,
    })
  }

  const closeModal = () => {
    setModalState({
      isOpen: false,
      mode: 'create',
      profile: undefined,
    })
  }

  return (
    <>
      <div className="flex h-14 items-center justify-between border-b border-gray-200/50 px-4">
        <div className="relative flex items-center gap-2">
          {isCollapsed ? (
            <Link
              href={PAGE_PATH.DASHBOARD}
              className="flex items-center gap-1 rounded-md hover:bg-gray-100 focus:outline-none"
            >
              <div className="relative h-10 w-10">
                <Image
                  alt="Sprites logo"
                  src="/sprites.svg"
                  width={40}
                  height={40}
                  className="h-10 w-10 cursor-pointer hover:opacity-80"
                />
              </div>
            </Link>
          ) : (
            <BusinessProfileDropdown
              onCreateProfile={openCreateModal}
              onEditProfile={openEditModal}
              onDeleteProfile={openDeleteModal}
            />
          )}
        </div>
        {!isCollapsed && (
          <div className="relative">
            <motion.button
              onClick={onToggleCollapse}
              onMouseEnter={() => {
                // Add delay before showing tooltip to prevent accidental displays
                tooltipTimerRef.current = setTimeout(() => {
                  setShowTooltip(true)
                }, 200)
              }}
              onMouseLeave={() => {
                // Clear any pending tooltip timer
                if (tooltipTimerRef.current) {
                  clearTimeout(tooltipTimerRef.current)
                  tooltipTimerRef.current = null
                }
                setShowTooltip(false)
              }}
              className="rounded-lg p-1 transition-colors hover:bg-gray-100"
              aria-label={isPermanentlyCollapsed ? 'Keep sidebar open' : 'Close sidebar'}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isPermanentlyCollapsed && isHovering ? (
                <FiChevronsRight className="h-5 w-5 text-gray-600" />
              ) : (
                <FiChevronsLeft className="h-5 w-5 text-gray-600" />
              )}
            </motion.button>
            {showTooltip && (
              <div className="absolute right-0 top-full z-50 mt-2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg">
                {isPermanentlyCollapsed && isHovering ? 'Keep sidebar open' : 'Close sidebar'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Profile Modal */}
      <BusinessProfileModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        mode={modalState.mode}
        profile={modalState.profile}
      />
    </>
  )
}
